import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { CacheWarmupService } from 'src/app/services/offline/cache-warmup.service';

@Component({
    selector: 'app-pedidos-pendiente-visualizarguia',
    templateUrl: './pedidos-pendiente-visualizarguia.component.html',
    styleUrls: ['./pedidos-pendiente-visualizarguia.component.css'],
    standalone: false
})
export class PedidosPendienteVisualizarguiaComponent implements OnInit, OnDestroy {
  tipoTexto = '';
  etapa = { sinData: false, MostrarData: false };

  @Input() rutaPdf: string =
    'https://www.polpaicoconexion.cl/intranet/facturas/pdfsn.asp?id=1167440&t=0&prod=3200';
  /** tkt is used to look up the cached PDF when offline */
  @Input() tkt: string = '';

  @ViewChild('contenidoModal') contenidoModal!: TemplateRef<any>;
  dialogRef: MatDialogRef<any>;

  @Input() isOnlyIcon = true;
  @Input() hide = false;
  @Input() openModal = false;
  @Input() numGuia = 0;
  @Output() modalStatusChange = new EventEmitter<boolean>();

  esPdf = false;
  esImagen = false;
  rutaDocumentoSafe!: SafeUrl;

  /** Resolved source used by the viewer and download — string URL (online) or Uint8Array (offline cache) */
  effectiveSrc: string | Uint8Array = '';
  private blobUrl: string | null = null;
  private pdfFilename: string = 'guia.pdf';

  /** Tracks whether the dialog is currently open. SWR swaps are skipped if the user
   *  closed the viewer before revalidation finished. */
  private isOpen = false;

  /** Set when setDocumento served from IndexedDB — signals that background revalidation
   *  should run. If we served from a fresh URL/fetch, the data is already current. */
  private renderedFromCache = false;

  // ─── Pinch-to-zoom ────────────────────────────────────────────────────────
  pdfZoom = 1;
  pdfPinchTransform = '';
  private _pinchStartDist = 0;
  private _pinchStartZoom = 1;
  private _pinchScale = 1;

  constructor(
    private location: Location,
    private dialog: MatDialog,
    private scrollStrategyOptions: ScrollStrategyOptions,
    public sanitizer: DomSanitizer,
    private offlineStorage: OfflineStorageService,
    private cacheWarmup: CacheWarmupService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.isOpen = false;
    this.revokeBlobUrl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.openModal && changes.openModal.currentValue) {
      this.onOpenModal();
    }
  }

  goBack() {
    this.onCierrenModal();
  }

  eventoDescarPdf() {
    const downloadUrl = this.blobUrl ?? (typeof this.effectiveSrc === 'string' ? this.effectiveSrc : null);
    if (!downloadUrl) return;
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = downloadUrl;
    a.download = this.pdfFilename;
    a.setAttribute('target', '_blank');
    a.click();
    document.body.removeChild(a);
    this.onCierrenModal();
  }

  async onOpenModal() {
    // Reset display state so *ngIf transitions false→true every open,
    // ensuring canvas viewer is freshly created each time
    this.esPdf = false;
    this.esImagen = false;
    this.etapa = { sinData: false, MostrarData: false };
    this.effectiveSrc = '';
    this.pdfZoom = 1;
    this.pdfPinchTransform = '';
    this._pinchScale = 1;
    this.renderedFromCache = false;
    this.isOpen = true;

    try {
      await this.setDocumento();
    } catch (e) {
      this.etapa.sinData = true;
    }

    // NgZone.run ensures Angular's change detection fires when we open the dialog.
    // The IDB callback inside setDocumento() can resume outside the zone, and calling
    // dialog.open() outside the zone means the embedded view is created without CD,
    // leaving the canvas viewer blank even though all properties are correctly set.
    this.ngZone.run(() => {
      this.etapa.MostrarData = true;
      this.dialogRef = this.dialog.open(this.contenidoModal, {
        width: '96vw',
        maxWidth: '1100px',
        maxHeight: '92vh',
        disableClose: true,
        panelClass: 'document-viewer-dialog',
        scrollStrategy: this.scrollStrategyOptions.noop(),
      });
    });

    // Stale-while-revalidate: if the first render came from the cache, kick off a
    // background re-fetch. This rescues the driver from the "guía siendo procesada"
    // placeholder — when the backend has the real PDF ready, we swap live without the
    // user having to close and reopen. Skipped on offline to avoid unnecessary waits.
    if (this.renderedFromCache && this.tkt && this.rutaPdf && navigator.onLine) {
      this.startBackgroundRevalidation();
    }
  }

  onCierrenModal() {
    this.isOpen = false;
    this.modalStatusChange.emit(false);
    this.dialogRef.close();
  }

  async setDocumento(): Promise<void> {
    this.revokeBlobUrl();

    // Always try cache first — works offline and avoids network on unstable connections.
    // A cached entry is only used if it decodes AND starts with the %PDF- magic bytes;
    // anything else is treated as poison (e.g. an HTML login page stored by a failed fetch)
    // and evicted so the next path falls through to a fresh network load.
    if (this.tkt) {
      const cached = await this.offlineStorage.getPdf(this.tkt);
      if (cached) {
        const bytes = this.decodeBase64Pdf(cached.base64);
        if (bytes && this.isPdfMagic(bytes)) {
          this.renderFromBytes(bytes, cached.filename);
          this.renderedFromCache = true;
          return;
        }
        console.warn('[VisualizarGuia] cached entry is not a valid PDF; evicting');
        await this.offlineStorage.clearTktCache(this.tkt).catch(() => null);
      }
    }

    // No valid cached version — fetch from URL (requires network).
    // savePdfToCache validates the response before writing, so it will never poison the cache.
    // If the fetch produces a valid PDF, re-read from cache so we render from the trusted bytes.
    if (this.tkt && this.rutaPdf) {
      const saved = await this.cacheWarmup.savePdfToCache(this.tkt, this.rutaPdf).catch(() => false);
      if (saved) {
        const cached = await this.offlineStorage.getPdf(this.tkt).catch(() => null);
        if (cached) {
          const bytes = this.decodeBase64Pdf(cached.base64);
          if (bytes && this.isPdfMagic(bytes)) {
            this.renderFromBytes(bytes, cached.filename);
            return;
          }
        }
      }
    }

    const url = this.rutaPdf.toLowerCase();
    this.esImagen =
      url.includes('.tif') ||
      url.includes('.tiff') ||
      url.includes('.jpg') ||
      url.includes('.jpeg') ||
      url.includes('.png');
    this.esPdf = !this.esImagen && (url.includes('.pdf') || (url.includes('pdf') && (url.includes('.asp') || url.includes('/pdf'))));
    this.effectiveSrc = this.rutaPdf;
    this.rutaDocumentoSafe = this.sanitizer.bypassSecurityTrustUrl(this.rutaPdf);

    try {
      const segment = new URL(this.rutaPdf).pathname.split('/').pop();
      this.pdfFilename = (segment && segment.toLowerCase().endsWith('.pdf'))
        ? segment
        : (this.numGuia ? `guia_${this.numGuia}.pdf` : 'guia.pdf');
    } catch {
      this.pdfFilename = this.numGuia ? `guia_${this.numGuia}.pdf` : 'guia.pdf';
    }
  }

  /** Returns true if the first 5 bytes are "%PDF-". */
  private isPdfMagic(bytes: Uint8Array): boolean {
    return bytes.length >= 5 &&
      bytes[0] === 0x25 && bytes[1] === 0x50 &&
      bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d;
  }

  /** Decodes the stored base64 payload (data URI or raw) into bytes, or null on failure. */
  private decodeBase64Pdf(stored: string): Uint8Array | null {
    try {
      const b64 = stored.includes(',') ? stored.split(',')[1] : stored;
      const raw = atob(b64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      return bytes;
    } catch (e) {
      console.warn('[VisualizarGuia] base64 decode failed:', e);
      return null;
    }
  }

  /** Binds the given PDF bytes to the viewer (effectiveSrc + blob URL for download). */
  private renderFromBytes(bytes: Uint8Array, filename: string): void {
    this.revokeBlobUrl();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    this.blobUrl = URL.createObjectURL(blob);
    this.effectiveSrc = bytes;
    this.pdfFilename = filename;
    this.esPdf = true;
    this.esImagen = false;
  }

  /**
   * Fires a background re-fetch of the PDF. If the server's current version differs
   * from what's in the cache (e.g. we showed the "guía siendo procesada" placeholder
   * and the real guía is now ready), the cache is updated and the viewer swaps live —
   * as long as the user hasn't closed the dialog in the meantime.
   *
   * The canvas viewer reacts to [src] input changes, so reassigning effectiveSrc is
   * enough to trigger a re-render. The viewer will briefly flash and reset scroll.
   */
  private async startBackgroundRevalidation(): Promise<void> {
    const result = await this.cacheWarmup
      .fetchAndCacheIfChanged(this.tkt, this.rutaPdf)
      .catch(() => null);

    if (!result) return;          // nothing changed, network failed, or non-PDF response
    if (!this.isOpen) return;      // user closed the viewer before we finished
    if (this.esImagen) return;     // defensive — SWR only applies to the PDF path

    // Re-enter the zone so Angular picks up the binding change.
    this.ngZone.run(() => this.renderFromBytes(result.bytes, result.filename));
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  // ─── Pinch-to-zoom handlers ───────────────────────────────────────────────

  onPdfTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 2) return;
    this._pinchStartDist = this._pinchDist(event);
    this._pinchStartZoom = this.pdfZoom;
    this._pinchScale = 1;
  }

  onPdfTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 2) return;
    event.preventDefault(); // block WebView scroll while pinching
    this._pinchScale = this._pinchDist(event) / this._pinchStartDist;
    this.pdfPinchTransform = `scale(${this._pinchScale})`;
  }

  onPdfTouchEnd(): void {
    if (this._pinchScale === 1) return;
    this.pdfPinchTransform = '';
    this.pdfZoom = Math.min(Math.max(this._pinchStartZoom * this._pinchScale, 0.5), 4);
    this._pinchScale = 1;
  }

  private _pinchDist(event: TouchEvent): number {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}


